import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, ArrowRight, Briefcase, Building2, Car, Loader2, UserRound, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { STATES, REASONS, LEVELS, BUSINESS_TYPES, GL_BUSINESS_TYPES, businessTypesFor, stateNote, stateName, levelLabel, limitLabel, limitsFor, reasonLabel, coverageLabel } from "@/lib/constants";

const DRAFT_KEY = "quote_draft_v2";
const YEARS = Array.from({ length: new Date().getFullYear() + 2 - 1981 }, (_, i) => String(new Date().getFullYear() + 1 - i));
const EMPTY = {
  state: "", coverage_type: "owner", reason: "", coverage_level: "liability", liability_limit: "10_20_10", vehicle_count: 1, employee_count: 1,
  business_name: "", business_type: "", dot_number: "",
  full_name: "", date_of_birth: "", license_number: "", phone: "", address: "", city: "", zip_code: "",
  vehicle_year: "", vehicle_make: "", vehicle_model: "", vin: "",
};
const STEP_NAMES = ["Your policy", "Driver details", "Coverage", "Review"];

const loadDraft = () => {
  try {
    const d = JSON.parse(sessionStorage.getItem(DRAFT_KEY)) || null;
    if (d?.form && !YEARS.includes(d.form.vehicle_year)) {
      d.form.vehicle_year = "";
      if (!["non_owner", "general_liability"].includes(d.form.coverage_type)) d.step = Math.min(d.step ?? 0, 2);
    }
    return d;
  } catch (e) {
    return null;
  }
};

const F = ({ label, id, children }) => (
  <div className="space-y-1.5">
    <Label htmlFor={id}>{label}</Label>
    {children}
  </div>
);

const ChoiceCard = ({ active, onClick, icon: Icon, title, desc, testId, disabled }) => (
  <button
    type="button"
    disabled={disabled}
    onClick={onClick}
    data-testid={testId}
    className={`flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-colors duration-150 disabled:cursor-not-allowed disabled:opacity-40 ${active ? "border-blue-700 bg-blue-50 ring-1 ring-blue-700" : "border-slate-200 bg-white hover:border-slate-400"}`}
  >
    {Icon && <Icon className={`mt-0.5 h-5 w-5 ${active ? "text-blue-700" : "text-slate-400"}`} />}
    <span>
      <span className="block font-semibold text-slate-900">{title}</span>
      <span className="block text-sm text-slate-500">{desc}</span>
    </span>
  </button>
);

const StepSituation = ({ f, set }) => (
  <div className="space-y-6">
    <F label="State where you need coverage" id="state">
      <Select value={f.state} onValueChange={(v) => set("state", v)}>
        <SelectTrigger id="state" data-testid="quote-wizard-state-select"><SelectValue placeholder="Select your state" /></SelectTrigger>
        <SelectContent className="max-h-72">
          {STATES.map((s) => <SelectItem key={s.code} value={s.code} data-testid={`state-option-${s.code}`}>{s.name}</SelectItem>)}
        </SelectContent>
      </Select>
    </F>
    {f.state && f.reason !== "none" && <p className="flex gap-2 rounded-md bg-slate-50 p-3 text-sm text-slate-600" data-testid="state-note"><Info className="mt-0.5 h-4 w-4 shrink-0 text-blue-700" />{stateNote(f.state)}</p>}
    <div className="space-y-1.5">
      <Label>What would you like to insure?</Label>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="quote-wizard-coverage-type-radio">
        <ChoiceCard testId="coverage-type-owner" active={f.coverage_type === "owner"} onClick={() => set("coverage_type", "owner")} icon={Car} title="Personal auto" desc="Insure a car I own. Limits from $10,000." />
        <ChoiceCard testId="coverage-type-non-owner" active={f.coverage_type === "non_owner"} onClick={() => set("coverage_type", "non_owner")} icon={UserRound} title="Non-owner auto" desc="I drive but don't own a car." />
        <ChoiceCard testId="coverage-type-commercial" active={f.coverage_type === "commercial"} onClick={() => set("coverage_type", "commercial")} icon={Building2} title="Commercial auto liability" desc="Vehicles used for my business." />
        <ChoiceCard testId="coverage-type-general-liability" active={f.coverage_type === "general_liability"} onClick={() => set("coverage_type", "general_liability")} icon={Briefcase} title="General liability" desc="Protect my business from injury and property damage claims." />
      </div>
    </div>
    {f.coverage_type !== "general_liability" && <F label="Do you need an SR22 filing?" id="reason">
      <Select value={f.reason} onValueChange={(v) => set("reason", v)}>
        <SelectTrigger id="reason" data-testid="quote-wizard-reason-select"><SelectValue placeholder="Choose one" /></SelectTrigger>
        <SelectContent>
          {REASONS.map((r) => <SelectItem key={r.value} value={r.value} data-testid={`reason-option-${r.value}`}>{r.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </F>}
  </div>
);

const StepDriver = ({ f, set }) => {
  const inp = (k, label, props = {}) => (
    <F label={label} id={k}>
      <Input id={k} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`quote-${k.replace(/_/g, "-")}-input`} {...props} />
    </F>
  );
  const gl = f.coverage_type === "general_liability";
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {(f.coverage_type === "commercial" || gl) && (
        <>
          <div className="sm:col-span-2">{inp("business_name", "Business name")}</div>
          <F label="Type of business" id="business_type">
            <Select value={f.business_type} onValueChange={(v) => set("business_type", v)}>
              <SelectTrigger id="business_type" data-testid="quote-business-type-select"><SelectValue placeholder="Choose one" /></SelectTrigger>
              <SelectContent>{businessTypesFor(f.coverage_type).map((b) => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}</SelectContent>
            </Select>
          </F>
          {!gl && inp("dot_number", "USDOT number (optional)")}
        </>
      )}
      <div className="sm:col-span-2">{inp("full_name", gl ? "Owner / contact full name" : f.coverage_type === "commercial" ? "Owner / primary driver full name" : "Full legal name")}</div>
      {!gl && inp("date_of_birth", "Date of birth", { type: "date" })}
      {!gl && inp("license_number", "Driver's license number")}
      {inp("phone", "Phone", { type: "tel" })}
      {inp("zip_code", "ZIP code")}
      <div className="sm:col-span-2">{inp("address", "Street address")}</div>
      {inp("city", "City")}
    </div>
  );
};

const StepCoverage = ({ f, set }) => (
  <div className="space-y-6">
    {f.coverage_type === "general_liability" && (
      <F label="Number of employees (including owners)" id="employee_count"><Input id="employee_count" type="number" min="1" max="500" value={f.employee_count} onChange={(e) => set("employee_count", Math.max(1, Math.min(500, Number(e.target.value) || 1)))} data-testid="quote-employee-count-input" /></F>
    )}
    {!["non_owner", "general_liability"].includes(f.coverage_type) && (
      <div className="grid gap-4 sm:grid-cols-3">
        {f.coverage_type === "commercial" && (
          <div className="sm:col-span-3">
            <F label="Number of business vehicles" id="vehicle_count"><Input id="vehicle_count" type="number" min="1" max="50" value={f.vehicle_count} onChange={(e) => set("vehicle_count", Math.max(1, Math.min(50, Number(e.target.value) || 1)))} data-testid="quote-vehicle-count-input" /></F>
          </div>
        )}
        <F label="Year" id="vehicle_year">
          <Select value={f.vehicle_year} onValueChange={(v) => set("vehicle_year", v)}>
            <SelectTrigger id="vehicle_year" data-testid="quote-vehicle-year-input"><SelectValue placeholder="Select year" /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
        </F>
        {[["vehicle_make", "Make"], ["vehicle_model", "Model"]].map(([k, l]) => (
          <F key={k} label={l} id={k}><Input id={k} value={f[k]} onChange={(e) => set(k, e.target.value)} data-testid={`quote-${k.replace(/_/g, "-")}-input`} /></F>
        ))}
        <div className="sm:col-span-3">
          <F label="Last 6 characters of VIN" id="vin"><Input id="vin" maxLength={6} placeholder="e.g. A12345" value={f.vin} onChange={(e) => set("vin", e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ""))} data-testid="quote-vin-input" /></F>
          <p className={`mt-1 text-xs ${f.vin && !/^[A-HJ-NPR-Z0-9]{6}$/.test(f.vin) ? "text-red-600" : "text-slate-500"}`} data-testid="quote-vin-hint">Enter exactly 6 characters. VINs never use the letters I, O or Q.</p>
        </div>
      </div>
    )}
    <div className="space-y-1.5">
      <Label>{f.coverage_type === "general_liability" ? "Limits of insurance" : "Liability limits"} {["owner", "non_owner"].includes(f.coverage_type) && <span className="font-normal text-slate-500">(bodily injury per person / per accident / property damage)</span>}</Label>
      <div className="grid gap-3 sm:grid-cols-2" data-testid="quote-limit-group">
        {limitsFor(f.coverage_type).map((l) => (
          <ChoiceCard key={l.value} testId={`limit-${l.value}`} active={f.liability_limit === l.value} onClick={() => set("liability_limit", l.value)} title={l.label} desc={l.desc} />
        ))}
      </div>
      <p className="text-xs text-slate-500">{f.coverage_type === "general_liability" ? "Covers bodily injury, property damage, personal & advertising injury and medical payments." : "If your state requires higher limits, your policy automatically meets the state minimum."}</p>
    </div>
    {f.coverage_type !== "general_liability" && <div className="space-y-1.5">
      <Label>Coverage level</Label>
      <div className="grid gap-3" data-testid="quote-coverage-level-group">
        {LEVELS.map((l) => (
          <ChoiceCard key={l.value} testId={`coverage-level-${l.value}`} active={f.coverage_level === l.value} disabled={l.ownerOnly && f.coverage_type === "non_owner"} onClick={() => set("coverage_level", l.value)} title={l.label} desc={l.ownerOnly && f.coverage_type === "non_owner" ? "Requires a vehicle you own." : l.desc} />
        ))}
      </div>
    </div>}
  </div>
);

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 border-b border-slate-100 py-2 text-sm">
    <span className="text-slate-500">{k}</span>
    <span className="text-right font-medium text-slate-900">{v || "—"}</span>
  </div>
);

const StepReview = ({ f }) => (
  <div data-testid="quote-review">
    <Row k="State" v={stateName(f.state)} />
    <Row k="Policy type" v={coverageLabel(f.coverage_type)} />
    {["commercial", "general_liability"].includes(f.coverage_type) && <Row k="Business" v={`${f.business_name} · ${businessTypesFor(f.coverage_type).find((b) => b.value === f.business_type)?.label || ""} · ${f.coverage_type === "commercial" ? `${f.vehicle_count} vehicle(s)` : `${f.employee_count} employee(s)`}`} />}
    {f.coverage_type !== "general_liability" && <Row k="SR22 filing" v={reasonLabel(f.reason)} />}
    {f.coverage_type !== "general_liability" && <Row k="Coverage" v={levelLabel(f.coverage_level)} />}
    <Row k="Liability limits" v={limitLabel(f.liability_limit)} />
    <Row k={f.coverage_type === "general_liability" ? "Contact" : "Driver"} v={f.full_name} />
    {f.coverage_type !== "general_liability" && <Row k="Date of birth" v={f.date_of_birth} />}
    {f.coverage_type !== "general_liability" && <Row k="License #" v={f.license_number} />}
    <Row k="Phone" v={f.phone} />
    {!["non_owner", "general_liability"].includes(f.coverage_type) && <Row k="Vehicle" v={`${f.vehicle_year} ${f.vehicle_make} ${f.vehicle_model} · VIN …${f.vin}`} />}
    <p className="mt-4 text-sm text-slate-500">Submit to get your final monthly price instantly. You can pay and download your policy right away.</p>
  </div>
);

const EstimatePanel = ({ est, loading, commercial }) => (
  <aside className="rounded-xl bg-slate-900 p-6 text-white lg:sticky lg:top-24" data-testid="quote-estimate-panel">
    <p className="eyebrow !text-blue-300">Instant estimate</p>
    {est ? (
      <>
        <p className="mt-4 font-mono text-4xl font-semibold" data-testid="quote-estimate-range-display">
          ${est.estimate_low} – ${est.estimate_high}<span className="text-base text-slate-400">/mo</span>
        </p>
        <p className="mt-2 text-sm text-slate-400">{est.filing_fee ? `+ $${est.filing_fee} one-time state filing fee` : "No filing fee"}</p>
      </>
    ) : (
      <p className="mt-4 text-sm text-slate-400" data-testid="quote-estimate-empty">{commercial ? "Choose your type of business on the next step to see a price range." : "Pick your state and SR22 answer to see a price range."}</p>
    )}
    {loading && <Loader2 className="mt-3 h-4 w-4 animate-spin text-slate-400" />}
    <p className="mt-6 border-t border-white/10 pt-4 text-xs text-slate-400">Estimate range. Your final price is shown instantly when you submit and is based on the details you provide.</p>
  </aside>
);

const canNext = (step, f) => {
  const biz = ["commercial", "general_liability"].includes(f.coverage_type);
  const gl = f.coverage_type === "general_liability";
  if (step === 0) return f.state && f.reason && f.coverage_type;
  if (step === 1) return f.full_name && (gl || (f.date_of_birth && f.license_number)) && f.phone.length >= 7 && (!biz || (f.business_name && f.business_type));
  if (step === 2) return ["non_owner", "general_liability"].includes(f.coverage_type) || (f.vehicle_year && f.vehicle_make && f.vehicle_model && /^[A-HJ-NPR-Z0-9]{6}$/.test(f.vin));
  return true;
};

export default function QuoteWizard() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const draft = loadDraft();
  const [f, setF] = useState(() => {
    const type = ["owner", "non_owner", "commercial", "general_liability"].includes(params.get("type")) ? params.get("type") : null;
    const base = { ...EMPTY, ...(draft?.form || {}), ...(params.get("state") ? { state: params.get("state") } : {}) };
    if (type && type !== base.coverage_type) {
      base.coverage_type = type;
      base.liability_limit = limitsFor(type)[0].value;
      if (type === "non_owner" || type === "general_liability") base.coverage_level = "liability";
      if (type === "general_liability") base.reason = "none";
      base.business_type = "";
    }
    return base;
  });
  const [step, setStep] = useState(draft?.step || 0);
  useEffect(() => {
    const src = params.get("src");
    if (src) localStorage.setItem("seutrade_src", src.slice(0, 30));
  }, [params]);
  const [est, setEst] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setF((p) => {
    const next = { ...p, [k]: v };
    if (k === "coverage_type") {
      next.liability_limit = limitsFor(v)[0].value;
      next.business_type = "";
      if (v === "general_liability") {
        next.reason = "none";
        next.coverage_level = "liability";
      } else if (p.coverage_type === "general_liability") {
        next.reason = "";
      }
      if (v === "non_owner" && p.coverage_level === "full") next.coverage_level = "liability";
    }
    return next;
  });

  useEffect(() => {
    if (!f.state || !f.reason) {
      setEst(null);
      return;
    }
    if (["commercial", "general_liability"].includes(f.coverage_type) && !f.business_type) {
      setEst(null);
      return;
    }
    setLoading(true);
    api.post("/quotes/estimate", {
      state: f.state, coverage_type: f.coverage_type, reason: f.reason, coverage_level: f.coverage_level,
      liability_limit: f.liability_limit, vehicle_count: f.vehicle_count, employee_count: f.employee_count, business_type: f.business_type || null,
      date_of_birth: f.date_of_birth || null,
    })
      .then((r) => setEst(r.data))
      .catch(() => setEst(null))
      .finally(() => setLoading(false));
  }, [f.state, f.coverage_type, f.reason, f.coverage_level, f.date_of_birth, f.liability_limit, f.vehicle_count, f.employee_count, f.business_type]);

  useEffect(() => {
    sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ form: f, step }));
  }, [f, step]);

  const submit = async () => {
    if (!user) {
      toast.info("Create a free account to save your quote.");
      nav("/register?next=/quote");
      return;
    }
    setBusy(true);
    try {
      await api.post("/quotes", { ...f, business_type: f.business_type || null, source: localStorage.getItem("seutrade_src") || "" });
      sessionStorage.removeItem(DRAFT_KEY);
      toast.success("Your final price is ready! Choose how to pay below.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const Steps = [StepSituation, StepDriver, StepCoverage, StepReview];
  const Current = Steps[step];
  return (
    <div className="grid-lines min-h-[calc(100vh-4rem)]">
      <div className="mx-auto grid max-w-6xl gap-8 px-4 py-12 sm:px-6 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <p className="eyebrow">Step {step + 1} of 4</p>
          <h1 className="mt-2 text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="quote-step-title">{step === 1 && f.coverage_type === "general_liability" ? "Business details" : STEP_NAMES[step]}</h1>
          <div className="mt-6 h-1.5 w-full overflow-hidden rounded-full bg-slate-200">
            <div className="h-full bg-blue-700 transition-[width] duration-300" style={{ width: `${((step + 1) / 4) * 100}%` }} data-testid="quote-progress-bar" />
          </div>
          <div className="mt-8 rounded-xl border border-slate-200 bg-white p-6 sm:p-8" key={step}>
            <div className="fade-up"><Current f={f} set={set} /></div>
            <div className="mt-8 flex justify-between">
              <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)} data-testid="quote-wizard-back-btn">
                <ArrowLeft className="mr-2 h-4 w-4" /> Back
              </Button>
              {step < 3 ? (
                <Button disabled={!canNext(step, f)} onClick={() => setStep(step + 1)} className="bg-blue-700 text-white hover:bg-blue-800 hover:text-white" data-testid="quote-wizard-next-btn">
                  Continue <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              ) : (
                <Button disabled={busy} onClick={submit} className="bg-emerald-600 text-white hover:bg-emerald-700 hover:text-white" data-testid="quote-wizard-submit-btn">
                  {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {user ? "Get my final price" : "Create account & get price"}
                </Button>
              )}
            </div>
          </div>
        </div>
        <div className="order-first lg:order-none lg:col-span-4 lg:pt-24">
          <EstimatePanel est={est} loading={loading} commercial={["commercial", "general_liability"].includes(f.coverage_type) && !f.business_type} />
        </div>
      </div>
    </div>
  );
}
